/**
 * Feature Flag Management System
 * Centralized feature flag configuration and management
 */

import EnvironmentConfig from "./environment.js";

export class FeatureFlags {
  // Core feature flags
  static get core() {
    return {
      // Authentication features
      socialLogin: this.getFlag("VUE_APP_FEATURE_SOCIAL_LOGIN", true),
      passwordReset: this.getFlag("VUE_APP_FEATURE_PASSWORD_RESET", true),
      emailVerification: this.getFlag(
        "VUE_APP_FEATURE_EMAIL_VERIFICATION",
        true
      ),

      // Profile features
      profilePictures: this.getFlag("VUE_APP_FEATURE_PROFILE_PICTURES", true),
      profileVerification: this.getFlag(
        "VUE_APP_FEATURE_PROFILE_VERIFICATION",
        false
      ),
      // Legacy generic flag, superseded by dbsStatus below. Retained because
      // existing code and docs reference it.
      backgroundChecks: this.getFlag(
        "VUE_APP_FEATURE_BACKGROUND_CHECKS",
        false
      ),

      // Six-feature programme (2026-07). All default off; each is enabled per
      // environment once its phase ships. See
      // docs/superpowers/specs/2026-07-25-crescendo-six-features-design.md
      teacherStudio: this.getFlag("VUE_APP_FEATURE_TEACHER_STUDIO", false),
      qualificationBadges: this.getFlag(
        "VUE_APP_FEATURE_QUALIFICATION_BADGES",
        false
      ),
      dbsStatus: this.getFlag("VUE_APP_FEATURE_DBS_STATUS", false),

      // Lesson features
      instantBooking: this.getFlag("VUE_APP_FEATURE_INSTANT_BOOKING", true),
      lessonRecording: this.getFlag("VUE_APP_FEATURE_LESSON_RECORDING", false),
      lessonNotes: this.getFlag("VUE_APP_FEATURE_LESSON_NOTES", true),
      lessonRating: this.getFlag("VUE_APP_FEATURE_LESSON_RATING", true),
      meetingLinks: this.getFlag("VUE_APP_FEATURE_MEETING_LINKS", true),
      lessonCancellation: this.getFlag(
        "VUE_APP_FEATURE_LESSON_CANCELLATION",
        true
      ),
      lessonReschedule: this.getFlag("VUE_APP_FEATURE_LESSON_RESCHEDULE", true),
      groupLessons: this.getFlag("VUE_APP_FEATURE_GROUP_LESSONS", true),
      teacherScheduling: this.getFlag(
        "VUE_APP_FEATURE_TEACHER_SCHEDULING",
        true
      ),

      // Communication features
      inAppMessaging: this.getFlag("VUE_APP_FEATURE_IN_APP_MESSAGING", false),
      videoCallIntegration: this.getFlag("VUE_APP_FEATURE_VIDEO_CALLS", false),
      notifications: this.getFlag("VUE_APP_FEATURE_NOTIFICATIONS", true),

      // Search and discovery
      advancedSearch: this.getFlag("VUE_APP_FEATURE_ADVANCED_SEARCH", true),
      teacherRecommendations: this.getFlag(
        "VUE_APP_FEATURE_TEACHER_RECOMMENDATIONS",
        false
      ),
      favoriteTeachers: this.getFlag("VUE_APP_FEATURE_FAVORITE_TEACHERS", true),
    };
  }

  // Payment and monetization features
  static get payments() {
    return {
      stripeIntegration: this.getFlag("VUE_APP_FEATURE_STRIPE_PAYMENTS", true),
      stripeConnect: this.getFlag("VUE_APP_FEATURE_STRIPE_CONNECT", true),
      mockPayments: this.getFlag(
        "VUE_APP_FEATURE_MOCK_PAYMENTS",
        EnvironmentConfig.isLocal
      ),
      subscriptionPlans: this.getFlag(
        "VUE_APP_FEATURE_SUBSCRIPTION_PLANS",
        false
      ),
      discountCodes: this.getFlag("VUE_APP_FEATURE_DISCOUNT_CODES", false),
      paymentHistory: this.getFlag("VUE_APP_FEATURE_PAYMENT_HISTORY", true),
      refunds: this.getFlag("VUE_APP_FEATURE_REFUNDS", true),
      tipTeachers: this.getFlag("VUE_APP_FEATURE_TIP_TEACHERS", false),
      packageDeals: this.getFlag("VUE_APP_FEATURE_PACKAGE_DEALS", false),
      firstLessonPromotions: this.getFlag(
        "VUE_APP_FEATURE_FIRST_LESSON_PROMOTIONS",
        true
      ),

      // Six-feature programme (2026-07).
      termBookings: this.getFlag("VUE_APP_FEATURE_TERM_BOOKINGS", false),
      incomeSummary: this.getFlag("VUE_APP_FEATURE_INCOME_SUMMARY", false),
    };
  }

  // Administrative features
  static get admin() {
    return {
      adminPanel: this.getFlag("VUE_APP_FEATURE_ADMIN_PANEL", true),
      userManagement: this.getFlag("VUE_APP_FEATURE_USER_MANAGEMENT", true),
      badgeManagement: this.getFlag("VUE_APP_FEATURE_BADGE_MANAGEMENT", true),
      reviewSystem: this.getFlag("VUE_APP_FEATURE_REVIEW_SYSTEM", true),
      reportGeneration: this.getFlag(
        "VUE_APP_FEATURE_REPORT_GENERATION",
        false
      ),
      auditLogs: this.getFlag(
        "VUE_APP_FEATURE_AUDIT_LOGS",
        EnvironmentConfig.isProduction
      ),
      systemHealth: this.getFlag(
        "VUE_APP_FEATURE_SYSTEM_HEALTH",
        !EnvironmentConfig.isProduction
      ),
      featureFlagToggle: this.getFlag(
        "VUE_APP_FEATURE_FLAG_TOGGLE",
        EnvironmentConfig.isDevelopment
      ),
    };
  }

  // Development and debugging features
  static get development() {
    return {
      debugPanel: this.getFlag(
        "VUE_APP_FEATURE_DEBUG_PANEL",
        // NEVER enable in production, even if flag is set
        EnvironmentConfig.isProduction
          ? false
          : EnvironmentConfig.shouldShowDebugInfo()
      ),
      environmentBanner: this.getFlag(
        "VUE_APP_FEATURE_ENV_BANNER",
        !EnvironmentConfig.isProduction
      ),
      performanceMetrics: this.getFlag(
        "VUE_APP_FEATURE_PERFORMANCE_METRICS",
        EnvironmentConfig.isDevelopment
      ),
      errorBoundary: this.getFlag("VUE_APP_FEATURE_ERROR_BOUNDARY", true),
      devTools: this.getFlag(
        "VUE_APP_FEATURE_DEV_TOOLS",
        EnvironmentConfig.isDevelopment
      ),
      mockData: this.getFlag(
        "VUE_APP_FEATURE_MOCK_DATA",
        EnvironmentConfig.isLocal
      ),
      testUtilities: this.getFlag(
        "VUE_APP_FEATURE_TEST_UTILITIES",
        !EnvironmentConfig.isProduction
      ),
    };
  }

  // External integrations
  static get integrations() {
    return {
      googleAnalytics: this.getFlag(
        "VUE_APP_FEATURE_GOOGLE_ANALYTICS",
        EnvironmentConfig.monitoring.analytics
      ),
      hotjar: this.getFlag(
        "VUE_APP_FEATURE_HOTJAR",
        EnvironmentConfig.monitoring.hotjar
      ),
      sentry: this.getFlag(
        "VUE_APP_FEATURE_SENTRY",
        EnvironmentConfig.monitoring.errorReporting
      ),
      // Crisp support chat is gated by the presence of VUE_APP_CRISP_WEBSITE_ID
      // (see src/services/auth.service.js), not a separate feature flag.
      calendlyIntegration: this.getFlag("VUE_APP_FEATURE_CALENDLY", false),

      // Six-feature programme (2026-07).
      calendarSyncIcs: this.getFlag("VUE_APP_FEATURE_CALENDAR_SYNC_ICS", false),
      // Stays false until the Google Cloud OAuth consent screen, verified
      // domain, Secret Manager and Pub/Sub channels are provisioned — see
      // docs/GOOGLE_CALENDAR_SETUP.md (design spec H-3).
      calendarSyncGoogle: this.getFlag(
        "VUE_APP_FEATURE_CALENDAR_SYNC_GOOGLE",
        false
      ),
      zoomIntegration: this.getFlag("VUE_APP_FEATURE_ZOOM", false),
      slackIntegrations: this.getFlag("VUE_APP_FEATURE_SLACK", false),
    };
  }

  // Performance and optimization features
  static get performance() {
    return {
      lazyLoading: this.getFlag("VUE_APP_FEATURE_LAZY_LOADING", true),
      imageCaching: this.getFlag("VUE_APP_FEATURE_IMAGE_CACHING", true),
      serviceWorker: this.getFlag(
        "VUE_APP_FEATURE_SERVICE_WORKER",
        EnvironmentConfig.isProduction
      ),
      preloadCritical: this.getFlag("VUE_APP_FEATURE_PRELOAD_CRITICAL", true),
      bundleAnalysis: this.getFlag(
        "VUE_APP_FEATURE_BUNDLE_ANALYSIS",
        EnvironmentConfig.isDevelopment
      ),
      compressionOptimization: this.getFlag(
        "VUE_APP_FEATURE_COMPRESSION",
        EnvironmentConfig.isProduction
      ),
    };
  }

  // Experimental features (use with caution)
  static get experimental() {
    return {
      aiRecommendations: this.getFlag(
        "VUE_APP_FEATURE_AI_RECOMMENDATIONS",
        false
      ),
      blockchainPayments: this.getFlag(
        "VUE_APP_FEATURE_BLOCKCHAIN_PAYMENTS",
        false
      ),
      voiceSearch: this.getFlag("VUE_APP_FEATURE_VOICE_SEARCH", false),
      arLessonView: this.getFlag("VUE_APP_FEATURE_AR_LESSONS", false),
      aiChatbot: this.getFlag("VUE_APP_FEATURE_AI_CHATBOT", false),
      predictiveBooking: this.getFlag(
        "VUE_APP_FEATURE_PREDICTIVE_BOOKING",
        false
      ),
    };
  }

  // Utility methods
  static getFlag(envVar, defaultValue = false) {
    const envValue = process.env[envVar];

    if (envValue === undefined) {
      return defaultValue;
    }

    if (typeof defaultValue === "boolean") {
      return envValue === "true";
    }

    if (typeof defaultValue === "number") {
      return parseInt(envValue, 10) || defaultValue;
    }

    return envValue;
  }

  // Get all feature flags as a flat object
  static getAllFlags() {
    return {
      ...this.core,
      ...this.payments,
      ...this.admin,
      ...this.development,
      ...this.integrations,
      ...this.performance,
      ...this.experimental,
    };
  }

  // Check if a specific feature is enabled
  static isEnabled(featureName) {
    const allFlags = this.getAllFlags();
    return allFlags[featureName] || false;
  }

  // Get features by category
  static getCategory(category) {
    switch (category.toLowerCase()) {
      case "core":
        return this.core;
      case "payments":
        return this.payments;
      case "admin":
        return this.admin;
      case "development":
        return this.development;
      case "integrations":
        return this.integrations;
      case "performance":
        return this.performance;
      case "experimental":
        return this.experimental;
      default:
        return {};
    }
  }

  // Get enabled features count by category
  static getEnabledCount(category = null) {
    const flags = category ? this.getCategory(category) : this.getAllFlags();
    return Object.values(flags).filter(Boolean).length;
  }

  // Environment-specific feature validation
  static validateFeatures() {
    const warnings = [];
    const errors = [];

    // Production-specific validations
    if (EnvironmentConfig.isProduction) {
      if (this.development.debugPanel) {
        errors.push(
          "CRITICAL: Debug panel is enabled in production - this should never happen!"
        );
      }
      if (this.development.mockData) {
        errors.push("Mock data feature is enabled in production");
      }
      if (this.payments.mockPayments) {
        errors.push("Mock payments feature is enabled in production");
      }
      if (this.development.devTools) {
        warnings.push("Dev tools are enabled in production");
      }
      if (this.development.testUtilities) {
        warnings.push("Test utilities are enabled in production");
      }
    }

    // Development-specific validations
    if (EnvironmentConfig.isDevelopment) {
      if (!this.development.debugPanel) {
        warnings.push("Debug panel is disabled in development");
      }
    }

    // Staging-specific validations
    if (EnvironmentConfig.isStaging) {
      if (this.payments.mockPayments && !this.payments.stripeIntegration) {
        warnings.push("Both mock and real payments are disabled in staging");
      }
    }

    // Feature dependency validations
    if (
      this.integrations.googleAnalytics &&
      !EnvironmentConfig.monitoring.googleAnalyticsId
    ) {
      warnings.push(
        "Google Analytics feature enabled but no Analytics ID configured"
      );
    }

    if (this.integrations.sentry && !EnvironmentConfig.monitoring.sentryDsn) {
      warnings.push("Sentry feature enabled but no Sentry DSN configured");
    }

    return { errors, warnings };
  }

  // Generate feature flag report
  static generateReport() {
    const categories = [
      "core",
      "payments",
      "admin",
      "development",
      "integrations",
      "performance",
      "experimental",
    ];
    const report = {
      environment: EnvironmentConfig.environment,
      totalFeatures: Object.keys(this.getAllFlags()).length,
      enabledFeatures: this.getEnabledCount(),
      categories: {},
    };

    categories.forEach((category) => {
      const categoryFlags = this.getCategory(category);
      report.categories[category] = {
        total: Object.keys(categoryFlags).length,
        enabled: Object.values(categoryFlags).filter(Boolean).length,
        flags: categoryFlags,
      };
    });

    const validation = this.validateFeatures();
    report.validation = validation;

    return report;
  }

  // Initialize feature flags (for debugging/logging)
  static initialize() {
    if (EnvironmentConfig.shouldShowDebugInfo()) {
      const report = this.generateReport();
      EnvironmentConfig.log("debug", "🎛️  Feature Flags Report:");
      EnvironmentConfig.log(
        "debug",
        `  Total Features: ${report.totalFeatures}`
      );
      EnvironmentConfig.log(
        "debug",
        `  Enabled Features: ${report.enabledFeatures}`
      );

      Object.entries(report.categories).forEach(([category, data]) => {
        EnvironmentConfig.log(
          "debug",
          `  ${category}: ${data.enabled}/${data.total} enabled`
        );
      });

      if (report.validation.warnings.length > 0) {
        EnvironmentConfig.log("warn", "⚠️  Feature Flag Warnings:");
        report.validation.warnings.forEach((warning) =>
          EnvironmentConfig.log("warn", `  - ${warning}`)
        );
      }

      if (report.validation.errors.length > 0) {
        EnvironmentConfig.log("error", "❌ Feature Flag Errors:");
        report.validation.errors.forEach((error) =>
          EnvironmentConfig.log("error", `  - ${error}`)
        );
      }
    }

    return this.generateReport();
  }
}

export default FeatureFlags;
